import { MenuItem, Select, useMediaQuery, Theme } from "@mui/material";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import {
  ActionFlightRefundTypeOptions,
  ENetwork,
  FlightRefundStatus,
  NetworkTypeOptions,
} from "@/types";
import { SelectChangeEvent } from "@mui/material/Select";
import SelectDate from "@/pages/refund/components/SelecDate";
interface RefundFiltersProps {
  startDate: string | undefined;
  endDate: string | undefined;
  onDateChange: (date: DateRange) => void;
  network: ENetwork | string;
  onNetworkChange: (event: SelectChangeEvent) => void;
  action: FlightRefundStatus | string;
  onActionChange: (event: SelectChangeEvent) => void;
  searchTerm: string;
  onSearch?: (value: string) => void;
  onApply?: () => void;
  onReset?: () => void;
}

const RefundFlightFilters = ({
  startDate,
  endDate,
  onDateChange,
  network,
  onNetworkChange,
  action,
  onActionChange,
  searchTerm,
  onSearch,
  onApply,
  onReset,
}: RefundFiltersProps) => {
  const [openNetwork, setOpenNetwork] = useState(false);
  const [openAction, setOpenAction] = useState(false);
  const [date, setDate] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const handleDateChange = (newDate: DateRange) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (openNetwork) setOpenNetwork(false);
      if (openAction) setOpenAction(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openNetwork, openAction]);

  useEffect(() => {
    if (startDate && endDate) {
      setDate({
        from: new Date(startDate),
        to: new Date(endDate),
      });
    } else {
      setDate({
        from: undefined,
        to: undefined,
      });
    }
  }, [startDate, endDate]);

  const handleReset = () => {
    setDate({
      from: undefined,
      to: undefined,
    });
    onReset?.();
  };

  return (
    <div className="flex w-full flex-1 flex-wrap justify-between gap-3 max-sm:mt-5 max-sm:flex-col">
      <div className="flex w-3/4 flex-col items-start gap-3 max-sm:w-full">
        <div className="flex w-full items-center gap-3 max-sm:flex-col">
          <div className="flex h-12 w-1/2 items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-4 max-sm:w-full ">
            <img
              src="/icons/search.svg"
              alt="search"
              className="aspect-square lg:size-5"
            />
            <input
              placeholder="Search Booking ID / Contact person"
              onChange={(e) => onSearch?.(e.target.value)}
              inputMode="search"
              value={searchTerm}
              className="w-full bg-white text-sm font-normal text-text outline-none placeholder:text-[#94A3B8]"
            />
          </div>

          <div className="w-1/2 max-sm:w-full">
            <div className="inline-flex w-full items-center rounded-md border border-[#E2E8F0] bg-white shadow-sm">
              <span className="flex h-12 w-20 items-center justify-center rounded-l-md border-r border-[#E2E8F0] bg-[#F6F9FB] text-sm font-semibold text-text">
                Date
              </span>
              <SelectDate
                mode="range"
                date={date}
                setDateSelect={handleDateChange}
                numberOfMonths={isMobile ? 1 : 2}
                contentStyle={{
                  width: isMobile ? "320px" : "",
                }}
                className="border-none"
                handleCloseUserMenu={() => {}}
                handleConform={() => {}}
                handleSelect={() => {}}
                anchorElUser={null}
              />
            </div>
          </div>
        </div>

        <div className="flex w-full items-center gap-3 max-sm:flex-col">
          <div className="w-1/2 max-sm:w-full">
            <Select
              open={openNetwork}
              onOpen={() => setOpenNetwork(true)}
              onClose={() => setOpenNetwork(false)}
              MenuProps={{
                disableScrollLock: true,
              }}
              displayEmpty
              onChange={onNetworkChange}
              value={(network as ENetwork) || ""}
              renderValue={(selected: ENetwork) => {
                if (!selected) {
                  return <span style={{ color: "#94A3B8" }}>All Network</span>;
                }
                if (selected === ENetwork.ALL) {
                  return "All Network";
                }
                const selectedNetwork = NetworkTypeOptions.find(
                  (n) => n.value === selected,
                );
                return selectedNetwork?.label || selected;
              }}
              sx={{
                cursor: "pointer",
                height: "48px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                },
                "& .MuiSelect-select": {
                  padding: "12px 14px",
                  color: network ? "#1E293B" : "#94A3B8",
                  fontSize: "14px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                },
              }}
              IconComponent={(props) => (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  {...props}
                  style={{
                    right: 8,
                    position: "absolute",
                    pointerEvents: "none",
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#94A3B8"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            >
              {Object.values(NetworkTypeOptions).map((network) => (
                <MenuItem
                  key={network.key}
                  value={network.value}
                  sx={{
                    color: "#475569",
                    fontSize: "14px",
                    padding: "10px 20px",
                  }}
                >
                  {network.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="w-1/2 max-sm:w-full">
            <Select
              open={openAction}
              onOpen={() => setOpenAction(true)}
              onClose={() => setOpenAction(false)}
              MenuProps={{
                disableScrollLock: true,
              }}
              displayEmpty
              onChange={onActionChange}
              value={(action as FlightRefundStatus | "ALL") || ""}
              renderValue={(selected: FlightRefundStatus | "ALL") => {
                if (!selected) {
                  return <span style={{ color: "#94A3B8" }}>All Action</span>;
                }
                if (selected === "ALL") {
                  return "All Action";
                }
                const selectedAction = ActionFlightRefundTypeOptions.find(
                  (a) => a.value === selected,
                );
                return selectedAction?.label || selected;
              }}
              sx={{
                cursor: "pointer",
                height: "48px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                },
                "& .MuiSelect-select": {
                  padding: "12px 14px",
                  color: action ? "#1E293B" : "#94A3B8",
                  fontSize: "14px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #E2E8F0",
                },
              }}
              IconComponent={(props) => (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  {...props}
                  style={{
                    right: 8,
                    position: "absolute",
                    pointerEvents: "none",
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#94A3B8"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            >
              {Object.values(ActionFlightRefundTypeOptions).map((action) => (
                <MenuItem
                  key={action.key}
                  value={action.value}
                  sx={{
                    color: "#475569",
                    fontSize: "14px",
                    padding: "10px 20px",
                  }}
                >
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-start justify-end gap-3 max-md:flex-1 max-sm:hidden">
        <button
          className="h-12 w-[63px] rounded-lg bg-text text-base font-medium text-white outline-none disabled:opacity-50 max-md:text-[12px] md:w-[100px] lg:h-12"
          onClick={onApply}
        >
          Apply
        </button>

        <button
          className="h-12 w-[63px] rounded-lg bg-text text-base font-medium text-white outline-none disabled:bg-neutral-100 disabled:text-neutral-400 max-md:text-[12px] md:w-[100px] lg:h-12"
          onClick={handleReset}
          disabled={
            !startDate && !endDate && !network && !action && !searchTerm
          }
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default RefundFlightFilters;
